// Re-attach the OOXML-only properties (font size, color, Google-Docs w:shd
// highlight, line spacing, real numbering formats) that mammoth structurally
// cannot emit onto the already-built Plate fragment.
//
// Anchoring: the extractor produced paragraphs in document order; we walk the
// fragment's paragraph-like blocks in the same order and pair them by
// normalized text with a forward-scanning cursor (so an occasional
// mammoth-only or OOXML-only paragraph self-heals instead of shifting
// everything after it).
//
// SAFETY: this pass is strictly ADDITIVE. It only fills a prop that is absent,
// only UPGRADES an already-detected list's marker (never creates a list), and
// never writes listStart — the load-time stampOrderedListStarts owns numbering.
// Run→leaf marks are applied by character-offset alignment and skipped entirely
// when the leaf/run text lengths disagree, so a mismatch can never corrupt text.
import { normalizeText } from './extract';
import type { ApplyOoxmlResult, OoxmlCell, OoxmlParagraph, OoxmlRun } from './types';

const PARAGRAPH_BLOCK_TYPES = new Set(['p', 'h1', 'h2', 'h3', 'blockquote']);
const CELL_TYPES = new Set(['td', 'th', 'table_cell', 'table_cell_header']);
const WHITESPACE = /\s/;

function isParagraphBlock(node: any): boolean {
    return node != null && typeof node.type === 'string' && PARAGRAPH_BLOCK_TYPES.has(node.type) && Array.isArray(node.children);
}

function isCell(node: any): boolean {
    return node != null && typeof node.type === 'string' && CELL_TYPES.has(node.type) && Array.isArray(node.children);
}

function collectLeaves(node: any, out: any[]): void {
    if (node == null) return;
    if (typeof node.text === 'string') {
        out.push(node);
        return;
    }
    if (Array.isArray(node.children)) {
        for (const child of node.children) collectLeaves(child, out);
    }
}

function blockText(node: any): string {
    const leaves: any[] = [];
    collectLeaves(node, leaves);
    return normalizeText(leaves.map((leaf) => leaf.text).join(''));
}

function applyRunToLeaf(leaf: any, run: OoxmlRun): void {
    if (run.fontSizePx && leaf.fontSize == null) leaf.fontSize = run.fontSizePx;
    if (run.color && leaf.color == null) leaf.color = run.color;
    if (run.backgroundColor && leaf.backgroundColor == null) leaf.backgroundColor = run.backgroundColor;
    if (run.underline && leaf.underline == null) leaf.underline = true;
}

// Attribute run marks to leaves by matching NON-WHITESPACE characters between
// the run stream and the leaf stream. Whitespace is exactly where upstream
// collapsing (collapseWhiteSpace) makes the two diverge, so ignoring it keeps
// the mapping robust without the brittle full-length equality this used to
// require (which silently dropped every mark — colors, w:shd highlights — on any
// paragraph whose whitespace had been collapsed). A leaf whose non-ws chars all
// fall in one run inherits that run's marks; a leaf spanning runs (mixed
// formatting) is left alone.
function applyRunsToLeaves(block: any, runs: OoxmlRun[]): void {
    if (runs.length === 0) return;
    const leaves: any[] = [];
    collectLeaves(block, leaves);
    if (leaves.length === 0) return;

    const runIndexByNonWs: number[] = [];
    runs.forEach((run, index) => {
        for (const ch of run.text) if (!WHITESPACE.test(ch)) runIndexByNonWs.push(index);
    });
    if (runIndexByNonWs.length === 0) return;

    // Both streams must cover the same visible text. If their non-whitespace
    // counts differ — e.g. the run stream carries more characters than the leaves
    // do — they didn't correspond, so don't guess marks off a mismatched
    // alignment. (Whitespace is excluded, so this stays robust to the upstream
    // whitespace collapsing described above.)
    let leafNonWs = 0;
    for (const leaf of leaves) {
        for (const ch of leaf.text as string) if (!WHITESPACE.test(ch)) leafNonWs += 1;
    }
    if (leafNonWs !== runIndexByNonWs.length) return;

    let cursor = 0;
    for (const leaf of leaves) {
        let firstRun = -1;
        let lastRun = -1;
        let exhausted = false;
        for (const ch of leaf.text as string) {
            if (WHITESPACE.test(ch)) continue;
            if (cursor >= runIndexByNonWs.length) {
                // Leaf has more visible text than the run stream — the two didn't
                // correspond; don't guess a mark for the uncovered tail.
                exhausted = true;
                break;
            }
            const runIndex = runIndexByNonWs[cursor];
            cursor += 1;
            if (firstRun === -1) firstRun = runIndex;
            lastRun = runIndex;
        }
        if (!exhausted && firstRun !== -1 && firstRun === lastRun) applyRunToLeaf(leaf, runs[firstRun]);
    }
}

function applyToBlock(node: any, paragraph: OoxmlParagraph): void {
    if (paragraph.align && node.align == null) node.align = paragraph.align;
    if (paragraph.lineHeight != null && node.lineHeight == null) node.lineHeight = paragraph.lineHeight;

    if (paragraph.list && node.listStyleType != null) {
        node.listStyleType = paragraph.list.listStyleType;
        if (node.indent == null) node.indent = paragraph.list.indent;
    }

    applyRunsToLeaves(node, paragraph.runs);
}

export function applyOoxmlProps(fragment: any[], paragraphs: OoxmlParagraph[], cells: OoxmlCell[] = []): ApplyOoxmlResult {
    let cursor = 0;
    let matched = 0;
    let skipped = 0;
    let cellCursor = 0;
    let cellsMatched = 0;

    const findParagraph = (text: string): number => {
        if (text === '') return -1;
        for (let i = cursor; i < paragraphs.length; i += 1) {
            if (paragraphs[i].anchorText === text) return i;
        }
        return -1;
    };

    const findCell = (text: string): number => {
        if (text === '') return -1;
        for (let i = cellCursor; i < cells.length; i += 1) {
            if (cells[i].anchorText === text) return i;
        }
        return -1;
    };

    const visit = (nodes: any[]): void => {
        for (const node of nodes) {
            if (isCell(node)) {
                const index = findCell(blockText(node));
                if (index >= 0) {
                    const { background } = cells[index];
                    // The cell renderer reads `background ?? backgroundColor`; only
                    // fill when the cell has no fill of its own yet.
                    if (background && node.background == null && node.backgroundColor == null) {
                        node.background = background;
                    }
                    cellCursor = index + 1;
                    cellsMatched += 1;
                }
                // Recurse for the cell's own paragraph blocks.
                if (Array.isArray(node.children)) visit(node.children);
            } else if (isParagraphBlock(node)) {
                const text = blockText(node);
                const index = findParagraph(text);
                if (index >= 0) {
                    applyToBlock(node, paragraphs[index]);
                    cursor = index + 1;
                    matched += 1;
                } else if (text !== '') {
                    skipped += 1;
                }
            } else if (Array.isArray(node?.children)) {
                visit(node.children);
            }
        }
    };

    visit(fragment);
    return { fragment, stats: { matched, skipped, cellsMatched } };
}
